import React, { useEffect, useRef } from 'react';
import Icone from '../primitivos/Icone';

/**
 * Progresso de um fluxo de várias etapas (at-58, frente C; ampliado na at-62).
 *
 * 🔴 **Requisito da spec 03 §7.2: o fluxo NÃO dizia em que etapa o consultor
 * está.** São seis telas em sequência — associado, veículo, documentos,
 * produtos, termo, assinatura — e nada informava quantas faltam.
 *
 * ⚠️ **Serve aos TRÊS fluxos** da adesão (adesão, substituição veicular e
 * substituição de titular — `plan-c` §1.4): os três têm as mesmas 6 etapas na
 * mesma sequência. Quem varia é só a rota de cada etapa, que é problema da tela.
 *
 * ## O que a at-62 acrescentou (pedido de Victor, 10/09/2026)
 *
 * *"Toda vez que estou na tela cotação-produtos, a bola dados fica verde, o que
 * dá a entender que os dados estão prontos, o que nem sempre é verdade."*
 *
 *   1. 🔴 **{@link PropsStepper.pendentes} — etapa anterior com pendência fica
 *      VERMELHA**, com o ícone de alerta. Antes, "estar atrás da etapa atual"
 *      bastava para a bolinha ficar verde com ✓: o indicador **afirmava** que
 *      aquela etapa estava pronta, sem saber nada sobre o conteúdo dela. Quem
 *      sabe é a tela, que passa a lista.
 *   2. 🔴 **{@link PropsStepper.aoIrPara} — as bolinhas viram botões**, para o
 *      consultor transitar entre as telas do fluxo.
 *   3. **A etapa atual é dita por extenso em qualquer largura.** Abaixo de 640px
 *      os rótulos das bolinhas não cabem, e o consultor via só círculos
 *      numerados.
 *   4. **A etapa atual é trazida para a área visível** quando a régua transborda.
 *
 * ⚠️ **Sem `aoIrPara` o componente continua exatamente como era**: indicador
 * inerte, itens `aria-hidden`, zero botões. É o que mantém os 11 consumidores do
 * fluxo de adesão inalterados — e há teste sentinela sobre isso.
 *
 * ⚠️ **A cor da pendência é VERMELHA aqui**, e isso diverge do `Chip`, onde
 * pendência é âmbar. É deliberado e foi pedido assim: o âmbar do `Chip` significa
 * "ainda não subiu ao SGA", que se resolve sozinho no cron; aqui significa
 * "faltam dados e o documento não vai sair", que só se resolve com ação de quem
 * está na tela.
 */

/** As 6 etapas do fluxo de adesão, na ordem. Rótulos curtos — cabem sob a bolinha. */
export const ETAPAS_ADESAO: readonly string[] = [
  'Associado',
  'Veículo',
  'Docs',
  'Produtos',
  'Termo',
  'Assinatura',
];

/**
 * As 2 etapas do fluxo de **cotação** (at-62, D1) — `/cotacao` e
 * `/cotacao-produtos`.
 *
 * ⚠️ **Não há terceira etapa nem tela terminal**: o PDF aparece na própria etapa
 * 2, então o indicador fica em 2/2 depois de gerar. Inventar um "Concluído" aqui
 * anunciaria uma tela que não existe.
 */
export const ETAPAS_COTACAO: readonly string[] = ['Dados', 'Produtos'];

/**
 * As 4 etapas do fluxo de **vistoria** (at-63, frente D/D2) — captura das fotos,
 * checklist de acessórios, assinatura do associado e o laudo.
 *
 * ⚠️ **O painel `/vistoria` NÃO é etapa.** Ele é a porta de entrada (a carteira
 * do consultor, uma linha por veículo) e a outra porta é a `/carteira`, que entra
 * direto no meio do fluxo — contar o painel faria a régua mentir para quem chega
 * por lá.
 *
 * ⚠️ A **prévia do laudo** (aberta a partir da assinatura) também não é etapa: é
 * um desvio de leitura de onde se volta para o mesmo lugar.
 */
export const ETAPAS_VISTORIA: readonly string[] = [
  'Fotos',
  'Checklist',
  'Assinatura',
  'Laudo',
];

interface PropsStepper {
  /** Etapa atual, começando em 1. */
  etapa: number;
  rotulos?: readonly string[];
  /**
   * Etapas (1-based) que estão **incompletas**, mesmo já tendo sido visitadas.
   *
   * ⚠️ A etapa **atual nunca** é pintada de vermelho, mesmo se listada aqui:
   * enquanto o consultor preenche, o que falta é o normal — alarmar seria ruído.
   * Quem cobra o que falta na etapa atual é a validação do formulário.
   */
  pendentes?: readonly number[];
  /**
   * Etapas que **não podem ser abertas agora**, e o motivo — `{ 3: 'já
   * cadastrado, não pode ser refeita' }`. O motivo entra no nome acessível do
   * botão, porque um alvo que não responde precisa dizer por quê.
   *
   * 🔴 **Não é enfeite: é o que impede um erro real.** No fluxo de adesão, a
   * etapa 3 é onde o `POST /associado/cadastrar` acontece — voltar lá depois do
   * cadastro devolveria **409** (CPF já cadastrado), porque a tela não tem
   * guarda para associado já criado. Quem sabe disso é o domínio; aqui só se
   * renderiza (convenção at-24/at-32/at-47).
   *
   * ⚠️ A bolinha desabilitada **mantém a cor do estado** (verde de concluída, por
   * exemplo): ela não deixou de ser um indicador por não ser clicável, e
   * esmaecer o lado esquerdo inteiro do fluxo faria a régua parecer quebrada.
   */
  desabilitadas?: Readonly<Record<number, string>>;
  /**
   * Torna as bolinhas clicáveis. Recebe a etapa (1-based); a tela decide a rota.
   *
   * 🔴 Ausente = indicador inerte (o comportamento das at-58/59/60). A tela só
   * deve passar isto quando sair dela **não perder trabalho**: numa tela de
   * formulário com estado local não gravado, navegar descarta o que foi
   * digitado. Ver como `/cotacao` resolve — lá o clique para a frente **grava**.
   */
  aoIrPara?: (etapa: number) => void;
  className?: string;
}

const Stepper: React.FC<PropsStepper> = ({
  etapa,
  rotulos = ETAPAS_ADESAO,
  pendentes = [],
  desabilitadas = {},
  aoIrPara,
  className = '',
}) => {
  const trilha = useRef<HTMLDivElement>(null);
  /*
   * ⚠️ Ref por CALLBACK e tipada em `HTMLElement`: a etapa atual é uma `<div>`
   * no modo indicador e um `<button>` no modo navegável, e um `RefObject`
   * tipado num dos dois não serve ao outro.
   */
  const bolinhaAtual = useRef<HTMLElement | null>(null);
  const guardarBolinhaAtual = (elemento: HTMLElement | null) => {
    bolinhaAtual.current = elemento;
  };

  /*
   * 🔴 Mantém a etapa atual VISÍVEL na trilha (at-62).
   *
   * Com 6 etapas numa tela de 360px a régua transborda, e o `overflow-x-auto`
   * começa sempre da esquerda: a partir da etapa 4 a bolinha atual nascia **fora
   * da área visível**, e o indicador de "onde estou" mostrava só as etapas já
   * vencidas. O consultor teria de arrastar para descobrir onde está.
   *
   * ⚠️ A rolagem é feita à mão no `scrollLeft` da trilha, **não** com
   * `scrollIntoView`. Aquele método pode rolar os ANCESTRAIS, e neste app isso
   * já causou defeito visível: o `html, body { overflow: hidden }` de
   * `tokens/base.css` impede o usuário de rolar mas não a rolagem programática,
   * e o shell subia e ficava preso (ver o sentinela do `RadioCards`). Mexer só
   * no `scrollLeft` do próprio container não tem como sair daqui.
   *
   * 📌 No jsdom todas as medidas são 0, então isto vira um no-op — nenhum teste
   * precisa de mock.
   */
  useEffect(() => {
    const container = trilha.current;
    const item = bolinhaAtual.current;
    if (!container || !item) return;

    const centro = item.offsetLeft - (container.clientWidth - item.clientWidth) / 2;
    container.scrollLeft = Math.max(0, centro);
  }, [etapa]);

  const total = rotulos.length;
  const rotuloAtual = rotulos[etapa - 1] ?? '';
  const navegavel = typeof aoIrPara === 'function';

  const rotulosPendentes = pendentes
    .filter((numero) => numero !== etapa)
    .map((numero) => rotulos[numero - 1])
    .filter(Boolean);

  return (
    <div className={className}>
      {/*
        🔴 A etapa atual DITA POR EXTENSO, em qualquer largura (at-62).
        Abaixo de 640px os rótulos das bolinhas não cabem e ficavam escondidos —
        o consultor via seis círculos numerados e nenhuma palavra. "Etapa 2 de 6"
        já situava; o que faltava era dizer **o que** é a etapa 2.

        ⚠️ `aria-hidden` porque isto repete, palavra por palavra, o `aria-label`
        do grupo abaixo — sem isso o leitor de tela anunciaria duas vezes.
      */}
      <div className="tw-flex tw-items-baseline tw-gap-2 tw-px-4 tw-pt-3" aria-hidden="true">
        <span className="tw-shrink-0 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
          Etapa {etapa} de {total}
        </span>
        <span className="tw-truncate tw-font-marca tw-text-sm tw-font-bold tw-text-brand">
          {rotuloAtual}
        </span>
      </div>

      <div
        ref={trilha}
        className={[
          'tw-flex tw-items-center tw-px-4 tw-pb-3 tw-pt-2',
          /* A trilha rola sozinha (ver o efeito acima) e também pelo gesto. */
          'tw-overflow-x-auto',
          /* A barra de rolagem aqui é ruído — o conteúdo é curto e o gesto é
             natural. Precisa dos DOIS (Firefox e WebKit), como no `AppShell`. */
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden',
        ].join(' ')}
        /*
         * Sem navegação, um rótulo só para o conjunto e os itens `aria-hidden` —
         * senão o leitor de tela leria "1 Associado 2 Veículo 3 Docs…" em toda
         * visita à tela, sem dizer o que importa: onde a pessoa está.
         *
         * ⚠️ Com navegação os itens PRECISAM ser alcançáveis (são botões), então
         * ali eles deixam de ser escondidos e cada um se nomeia por extenso.
         */
        role="group"
        aria-label={
          `Etapa ${etapa} de ${total}: ${rotuloAtual}` +
          (rotulosPendentes.length > 0
            ? `. Com pendências: ${rotulosPendentes.join(', ')}`
            : '')
        }
      >
        {rotulos.map((rotulo, indice) => {
          const numero = indice + 1;
          const atual = numero === etapa;
          // A etapa atual nunca é pintada de pendente — ver a prop `pendentes`.
          const pendente = !atual && pendentes.includes(numero);
          const concluida = !atual && !pendente && numero < etapa;

          const motivoIndisponivel = atual ? undefined : desabilitadas[numero];

          const estado = atual
            ? 'etapa atual'
            : pendente
              ? 'com pendências'
              : concluida
                ? 'concluída'
                : 'não iniciada';

          /*
           * O nome acessível junta o ESTADO e, quando houver, o motivo de não
           * dar para abrir — "Etapa 3: Docs — concluída, já cadastrado, não pode
           * ser refeita". Sem o motivo, o alvo que não responde não se explica.
           */
          const situacao = motivoIndisponivel ? `${estado}, ${motivoIndisponivel}` : estado;

          const miolo = (
            <>
              <span
                className={[
                  'tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full',
                  'tw-text-xs tw-font-bold tw-transition-colors',
                  pendente
                    ? 'tw-bg-danger tw-text-white'
                    : concluida
                      ? 'tw-bg-success tw-text-white'
                      : atual
                        ? 'tw-bg-brand tw-text-white tw-ring-4 tw-ring-brand/20'
                        : 'tw-bg-superficie-borda tw-text-texto-desabilitado',
                ].join(' ')}
              >
                {/*
                  ⚠️ Forma diferente por estado, não só cor: ✓ concluída,
                  triângulo de alerta pendente, número nas demais. A regra do
                  projeto (spec 02 §9 / at-54) é que marcador de estado precisa
                  ser legível sem depender de cor.
                */}
                {pendente ? (
                  <Icone nome="alerta" tamanho={14} traco={2.5} />
                ) : concluida ? (
                  <Icone nome="check" tamanho={12} traco={2.5} />
                ) : (
                  numero
                )}
              </span>

              {/* O rótulo por bolinha só aparece a partir de 640px: abaixo disso
                  as seis palavras não cabem, e quem diz onde estamos é a linha
                  acima. No desktop ele dá o mapa inteiro do fluxo. */}
              <span
                className={[
                  'tw-hidden tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-wide sm:tw-block',
                  pendente
                    ? 'tw-text-danger'
                    : atual
                      ? 'tw-text-brand'
                      : 'tw-text-texto-desabilitado',
                ].join(' ')}
              >
                {rotulo}
              </span>
            </>
          );

          return (
            <div
              key={rotulo}
              className="tw-flex tw-items-center"
              aria-hidden={navegavel ? undefined : true}
            >
              {navegavel ? (
                <button
                  type="button"
                  ref={atual ? guardarBolinhaAtual : undefined}
                  onClick={() => aoIrPara?.(numero)}
                  disabled={Boolean(motivoIndisponivel) || atual}
                  aria-current={atual ? 'step' : undefined}
                  aria-label={`Etapa ${numero}: ${rotulo} — ${situacao}`}
                  className={[
                    'tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-lg',
                    'tw-border-0 tw-bg-transparent tw-p-1 tw-transition-colors',
                    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/50',
                    /* ⚠️ A bolinha desabilitada NÃO é esmaecida — ela continua
                       sendo o indicador de estado (ver a prop `desabilitadas`).
                       O que ela perde é o cursor e o realce de hover. */
                    motivoIndisponivel
                      ? 'tw-cursor-not-allowed'
                      : atual
                        ? 'tw-cursor-default'
                        : 'tw-cursor-pointer hover:tw-bg-superficie',
                  ].join(' ')}
                >
                  {miolo}
                </button>
              ) : (
                <div
                  ref={atual ? guardarBolinhaAtual : undefined}
                  className="tw-flex tw-flex-col tw-items-center tw-gap-1"
                >
                  {miolo}
                </div>
              )}

              {indice < total - 1 && (
                <div
                  className={[
                    'tw-mx-1 tw-h-0.5 tw-w-6 tw-shrink-0 sm:tw-w-8',
                    /* Só verde quando a etapa da esquerda está realmente
                       concluída — com pendência a linha fica neutra, senão o
                       traço afirmaria o progresso que a bolinha acabou de negar. */
                    concluida ? 'tw-bg-success' : 'tw-bg-superficie-borda',
                  ].join(' ')}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stepper;
